import type { QueryClient } from "@tanstack/react-query";
import { invalidateIntelligence } from "@/lib/intelligence";
import { apiFetch } from "@/lib/api";

export const MEAL_REFRESH_KEY = ["meal-metric-refresh"] as const;
export interface MealRefreshState {
  status: "updating" | "failed" | "fresh";
  dates: (string | undefined)[];
}
const refreshRuns = new WeakMap<QueryClient, Promise<void>>();

/**
 * The complete set of caches that depend on a user's food log. Every
 * food-logging entry point must refresh all of these so that recent/frequent
 * lists, the dashboard, the daily snapshot/forecast, the coach AI, the
 * Plate Talk briefing, and the cross-feature intelligence signal cards all stay
 * in sync after a log is saved.
 *
 * Listing each invalidation by hand in every handler is how refreshes get
 * forgotten (e.g. the intelligence signal cards going stale). Funnel every
 * logging path through {@link invalidateAfterLog} instead so it is impossible
 * to miss one.
 *
 * Invalidating a prefix like `["/api/logs"]` also covers parameterised keys
 * such as `["/api/logs", { date }]` because React Query matches query keys by
 * prefix. Keys that are sibling strings (`["/api/logs/recent"]`) are distinct
 * and listed separately.
 */
const POST_LOG_QUERY_KEYS: readonly (readonly unknown[])[] = [
  ["/api/logs"],
  ["/api/logs/recent"],
  ["/api/logs/frequent"],
  ["/api/foods"],
  ["/api/snapshots"],
  ["/api/dashboard"],
  ["/api/forecast"],
  ["/api/plate-talk/daily-briefing"],
  ["coach-ai"],
  ["deep-command-daily"],
  ["neural-brief"],
  ["/api/nrs/current"],
  ["/api/smart-swap"],
  ["/api/personalization"],
  ["/api/personalization-core"],
  ["insights"],
  ["nrs-current"],
  ["nrs-history"],
];

/**
 * Perform every post-log cache invalidation in one call, including the
 * cross-feature intelligence context. Call this from every food-logging handler
 * instead of hand-listing query keys.
 */
function isMealDependentQuery(query: { queryKey: readonly unknown[] }) {
    const first = query.queryKey[0];
    return typeof first === "string" && POST_LOG_QUERY_KEYS.some(([prefix]) =>
      typeof prefix === "string" &&
      (first === prefix || (prefix.startsWith("/api/") && first.startsWith(`${prefix}/`))),
    );
}

/**
 * Cross-device reconciliation is read-only: the writer owns recomputation.
 * Reuse the meal freshness boundary, but do not replay writes, change dates,
 * clear a local refresh failure, or interrupt an in-flight local save refresh.
 * Inactive historical entries become stale and refresh when opened.
 */
export function reconcileMealReads(qc: QueryClient): Promise<void> {
  // Deliberately narrower than POST_LOG_QUERY_KEYS: queryFns are not
  // necessarily reads. In particular coach-ai/recommendations generates paid
  // output via POST, and must never run because an idle timer fired.
  const safeReadKeys = new Set([
    "/api/dashboard/kpis",
    "/api/dashboard/status-distribution",
    "/api/dashboard/group-health",
    "/api/dashboard/neural-brief",
    "/api/logs",
    "/api/logs/recent",
    "/api/logs/frequent",
    "/api/logs/logged-dates",
    "deep-command-daily",
    "neural-brief",
  ]);
  return qc.invalidateQueries({
    predicate: query => {
      const first = query.queryKey[0];
      return typeof first === "string" && (
        safeReadKeys.has(first) ||
        first === "/api/snapshots" || first.startsWith("/api/snapshots/")
      );
    },
    refetchType: "active",
  }, { cancelRefetch: false, throwOnError: true });
}

export async function invalidateAfterLog(qc: QueryClient, dates: readonly (string | undefined)[] = []): Promise<void> {
  // Cancel pre-save reads so they cannot overwrite the post-save result.
  const predicate = isMealDependentQuery;
  const previous = qc.getQueryData<MealRefreshState>(MEAL_REFRESH_KEY);
  const affected = [...new Set([
    ...(previous?.status !== "fresh" ? previous?.dates ?? [] : []),
    ...(dates.length ? dates : [undefined]),
  ])];
  qc.setQueryData<MealRefreshState>(MEAL_REFRESH_KEY, { status: "updating", dates: affected });
  const owner = qc.getQueryCache().find({ queryKey: MEAL_REFRESH_KEY, exact: true });
  const stillOwned = () => qc.getQueryCache().find({ queryKey: MEAL_REFRESH_KEY, exact: true }) === owner;
  // Matrix scores read committed meals directly, not the derived snapshot.
  // Refresh them immediately, even if snapshot recomputation is slow, queued,
  // or fails. Consume errors here so a slow snapshot cannot leave an unhandled
  // rejection; the normal refresh below still owns the retry/status boundary.
  const matrixRefresh = qc.cancelQueries({ predicate }).then(async () => {
    if (!stillOwned()) return;
    await qc.invalidateQueries({ queryKey: ["deep-command-daily"] }, { throwOnError: true });
  }).catch(() => undefined);
  // Serialize refreshes, not meal writes. A later save always gets its own
  // recomputation after any older refresh finishes. Clearing the account cache
  // fences the continuation so it cannot refill the next account's caches.
  const run = (refreshRuns.get(qc) ?? Promise.resolve()).then(async () => {
    if (!stillOwned()) return;
    try {
      for (const date of affected) {
        if (!stillOwned()) return;
        await apiFetch("/logs/refresh-snapshot", {
          method: "POST",
          body: JSON.stringify({ date }),
        });
      }
      if (!stillOwned()) return;
      await qc.cancelQueries({ predicate });
      await qc.cancelQueries({ queryKey: ["/api/intelligence"] });
      await Promise.all([
        qc.invalidateQueries({ predicate }, { throwOnError: true }),
        invalidateIntelligence(qc),
      ]);
      if (qc.getQueryCache().findAll({ queryKey: ["/api/intelligence"], type: "active" })
        .some(query => query.state.status === "error")) {
        throw new Error("Intelligence refresh failed");
      }
      if (stillOwned() && refreshRuns.get(qc) === run) {
        qc.setQueryData<MealRefreshState>(MEAL_REFRESH_KEY, { status: "fresh", dates: [] });
      }
    } catch {
      // The meal write succeeded. Never reject into a caller's "save failed"
      // handler or retry that write; only recomputation may be retried.
      if (stillOwned()) await qc.invalidateQueries({ queryKey: ["/api/logs"] });
      if (stillOwned() && refreshRuns.get(qc) === run) {
        qc.setQueryData<MealRefreshState>(MEAL_REFRESH_KEY, { status: "failed", dates: affected });
      }
    }
  });
  refreshRuns.set(qc, run);
  await Promise.all([matrixRefresh, run]);
}
